import { useState, useEffect, useCallback } from 'react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'

interface DatosCompletos {
  _id?: string
  fid: number
  codigo: string
  nombre_microcuenca: string
  municipio: string
  superficie_agricola?: number
  superficie_microcuenca?: number
  superficie_pasto?: number
  superficie_forestal?: number
  densidad_poblacion?: number
  poblacion_total?: number
  indice_marginacion?: number
  grado_marginacion?: string
  indice_rezago?: number
  clase_rezago?: string
  precipitacion?: number
  temperatura?: number
  altitud?: number
  pendiente?: number
  superficie_deforestada?: number
  erosion?: number
  categoria_erosion?: string
  nivel_erosion?: number
  ponderacion_escurrimiento?: number
  hectareas_anp?: number
  createdAt?: string
  updatedAt?: string
  creator?: string
}

export const useDatosCompletosCRUD = () => {
  const [datosCompletos, setDatosCompletos] = useState<DatosCompletos[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  const fetchDatos = useCallback(async () => {
    setLoading(true)
    try {
      const response = await lumi.entities.datos_completos.list({
        sort: { createdAt: -1 }
      })
      setDatosCompletos(response.list || [])
    } catch (error: any) {
      console.error('Error al cargar datos completos:', error)
      toast.error('Error al cargar los datos')
    } finally {
      setLoading(false)
    }
  }, [])

  const createDato = async (data: Partial<DatosCompletos>) => {
    try {
      const newData = {
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creator: 'user'
      }
      await lumi.entities.datos_completos.create(newData)
      toast.success('Registro creado exitosamente')
      await fetchDatos()
    } catch (error: any) {
      console.error('Error al crear registro:', error)
      toast.error('Error al crear el registro')
      throw error
    }
  }

  const updateDato = async (id: string, data: Partial<DatosCompletos>) => {
    try {
      const updateData = {
        ...data,
        updatedAt: new Date().toISOString()
      }
      await lumi.entities.datos_completos.update(id, updateData)
      toast.success('Registro actualizado exitosamente')
      await fetchDatos()
    } catch (error: any) {
      console.error('Error al actualizar registro:', error)
      toast.error('Error al actualizar el registro')
      throw error
    }
  }

  const deleteDato = async (id: string) => {
    try {
      await lumi.entities.datos_completos.delete(id)
      toast.success('Registro eliminado exitosamente')
      await fetchDatos()
    } catch (error: any) {
      console.error('Error al eliminar registro:', error)
      toast.error('Error al eliminar el registro')
      throw error
    }
  }

  const uploadExcelFromURL = async (url: string) => {
    setUploading(true)
    try {
      const response = await fetch(url, {
        mode: 'cors',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      })
      
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`)
      }
      
      const arrayBuffer = await response.arrayBuffer()
      const workbook = XLSX.read(arrayBuffer, { type: 'array' })
      const sheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[sheetName]
      const jsonData = XLSX.utils.sheet_to_json(worksheet)

      const processedData = jsonData.map((row: any) => ({
        fid: parseInt(row.fid || row.FID) || 0,
        codigo: row.CODIGO || row.codigo || '',
        nombre_microcuenca: row.MICROCUENCA || row.Nombre_De_Microcuenca || row.nombre_microcuenca || '',
        municipio: row.MUNICIPIO || row.Municipio || row.municipio || '',
        superficie_agricola: parseFloat(row['Superficie Agricola'] || row.Superficie_agricola || row.superficie_agricola) || 0,
        superficie_microcuenca: parseFloat(row['Sup_Micro_'] || row.Superficie_Microcuenca || row.superficie_microcuenca) || 0,
        superficie_pasto: parseFloat(row['Superficie Pasto'] || row.Superficie_pasto || row.superficie_pasto) || 0,
        superficie_forestal: parseFloat(row['Superficie Forestal'] || row.Superficie_forestal || row.superficie_forestal) || 0,
        densidad_poblacion: parseFloat(row['Densidad de población'] || row.Densidad_de_poblacion || row.densidad_poblacion) || 0,
        poblacion_total: parseFloat(row.Poblacion_total || row.poblacion_total) || 0,
        indice_marginacion: parseFloat(row.Indice_de_marginacion || row.indice_marginacion) || 0,
        grado_marginacion: row.Grado_de_indice_de_marginacion || row.grado_marginacion || '',
        indice_rezago: parseFloat(row.Indice_de_rezago || row.indice_rezago) || 0,
        clase_rezago: row.Clase_de_rezago || row.clase_rezago || '',
        precipitacion: parseFloat(row['Precipitación'] || row.Precipitacion || row.precipitacion) || 0,
        temperatura: parseFloat(row['Temperatura'] || row.Temperatura_media || row.temperatura) || 0,
        altitud: parseFloat(row.Altitud || row.altitud) || 0,
        pendiente: parseFloat(row.Pendiente || row.pendiente) || 0,
        superficie_deforestada: parseFloat(row.Superficie_deforestada || row.superficie_deforestada) || 0,
        erosion: parseFloat(row.Erosion || row.erosion) || 0,
        categoria_erosion: row.Categoria_de_erosion || row.categoria_erosion || '',
        nivel_erosion: parseFloat(row.Nivel_de_erosion || row.nivel_erosion) || 0,
        ponderacion_escurrimiento: parseFloat(row.Ponderacion_de_escurrimiento || row.ponderacion_escurrimiento) || 0,
        hectareas_anp: parseFloat(row.Hectareas_en_ANP || row.hectareas_anp) || 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creator: 'excel_import'
      }))

      const batchSize = 10
      let successCount = 0
      
      for (let i = 0; i < processedData.length; i += batchSize) {
        const batch = processedData.slice(i, i + batchSize)
        try {
          const promises = batch.map((data: any) => 
            lumi.entities.datos_completos.create(data)
          )
          await Promise.all(promises)
          successCount += batch.length
        } catch (batchError) {
          console.error(`Error en lote ${i / batchSize + 1}:`, batchError)
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} registros importados exitosamente`)
        await fetchDatos()
      }
    } catch (error: any) {
      console.error('Error al importar Excel:', error)
      toast.error('Error al importar el archivo')
      throw error
    } finally {
      setUploading(false)
    }
  }

  const uploadExcelFile = async (file: File) => {
    setUploading(true)
    try {
      const arrayBuffer = await file.arrayBuffer()
      const workbook = XLSX.read(arrayBuffer, { type: 'array' })
      const sheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[sheetName]
      const jsonData = XLSX.utils.sheet_to_json(worksheet)

      const processedData = jsonData.map((row: any) => ({
        fid: parseInt(row.fid || row.FID) || 0,
        codigo: row.CODIGO || row.codigo || '',
        nombre_microcuenca: row.MICROCUENCA || row.Nombre_De_Microcuenca || row.nombre_microcuenca || '',
        municipio: row.MUNICIPIO || row.Municipio || row.municipio || '',
        superficie_agricola: parseFloat(row['Superficie Agricola'] || row.Superficie_agricola || row.superficie_agricola) || 0,
        superficie_microcuenca: parseFloat(row['Sup_Micro_'] || row.Superficie_Microcuenca || row.superficie_microcuenca) || 0,
        superficie_pasto: parseFloat(row['Superficie Pasto'] || row.Superficie_pasto || row.superficie_pasto) || 0,
        superficie_forestal: parseFloat(row['Superficie Forestal'] || row.Superficie_forestal || row.superficie_forestal) || 0,
        densidad_poblacion: parseFloat(row['Densidad de población'] || row.Densidad_de_poblacion || row.densidad_poblacion) || 0,
        poblacion_total: parseFloat(row.Poblacion_total || row.poblacion_total) || 0,
        indice_marginacion: parseFloat(row.Indice_de_marginacion || row.indice_marginacion) || 0,
        grado_marginacion: row.Grado_de_indice_de_marginacion || row.grado_marginacion || '',
        indice_rezago: parseFloat(row.Indice_de_rezago || row.indice_rezago) || 0,
        clase_rezago: row.Clase_de_rezago || row.clase_rezago || '',
        precipitacion: parseFloat(row['Precipitación'] || row.Precipitacion || row.precipitacion) || 0,
        temperatura: parseFloat(row['Temperatura'] || row.Temperatura_media || row.temperatura) || 0,
        altitud: parseFloat(row.Altitud || row.altitud) || 0,
        pendiente: parseFloat(row.Pendiente || row.pendiente) || 0,
        superficie_deforestada: parseFloat(row.Superficie_deforestada || row.superficie_deforestada) || 0,
        erosion: parseFloat(row.Erosion || row.erosion) || 0,
        categoria_erosion: row.Categoria_de_erosion || row.categoria_erosion || '',
        nivel_erosion: parseFloat(row.Nivel_de_erosion || row.nivel_erosion) || 0,
        ponderacion_escurrimiento: parseFloat(row.Ponderacion_de_escurrimiento || row.ponderacion_escurrimiento) || 0,
        hectareas_anp: parseFloat(row.Hectareas_en_ANP || row.hectareas_anp) || 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creator: 'excel_upload'
      }))

      const batchSize = 10
      let successCount = 0
      
      for (let i = 0; i < processedData.length; i += batchSize) {
        const batch = processedData.slice(i, i + batchSize)
        try {
          const promises = batch.map((data: any) => 
            lumi.entities.datos_completos.create(data)
          )
          await Promise.all(promises)
          successCount += batch.length
        } catch (batchError) {
          console.error(`Error en lote ${i / batchSize + 1}:`, batchError)
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} registros importados exitosamente`)
        await fetchDatos()
      }
    } catch (error: any) {
      console.error('Error al importar Excel:', error)
      toast.error('Error al importar el archivo')
      throw error
    } finally {
      setUploading(false)
    }
  }

  const clearAllData = async () => {
    try {
      const ids = datosCompletos.map(d => d._id!).filter(Boolean)
      if (ids.length > 0) {
        const batchSize = 20
        let deletedCount = 0
        
        for (let i = 0; i < ids.length; i += batchSize) {
          const batch = ids.slice(i, i + batchSize)
          try {
            await lumi.entities.datos_completos.deleteMany(batch)
            deletedCount += batch.length
          } catch (batchError) {
            console.error(`Error eliminando lote ${i / batchSize + 1}:`, batchError)
          }
        }
        
        setDatosCompletos([])
        toast.success(`${deletedCount} registros eliminados exitosamente`)
      }
    } catch (error: any) {
      console.error('Error al limpiar datos:', error)
      toast.error('Error al limpiar los datos')
      throw error
    }
  }

  useEffect(() => {
    fetchDatos()
  }, [fetchDatos])

  return {
    datosCompletos,
    loading,
    uploading,
    createDato,
    updateDato,
    deleteDato,
    uploadExcelFromURL,
    uploadExcelFile,
    clearAllData,
    refetch: fetchDatos
  }
}
